import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import { Spin, Tooltip } from 'antd';

import { useIsMobile } from 'packages/utils';

import styles from './styles.module.less';

const COLOR_TYPES = {
  primary: styles.primary,
  secondary: styles.secondary,
  danger: styles.danger,
  dark: styles.dark,
  soft: styles.soft,
  default: styles.default,
  light: styles.light,
  white: styles.white,
};

const BACKGROUND_TYPES = {
  primary: styles.primaryBackground,
  secondary: styles.secondaryBackground,
  danger: styles.dangerBackground,
  dark: styles.darkBackground,
  soft: styles.softBackground,
  default: styles.defaultBackground,
  light: styles.lightBackground,
  white: styles.whiteBackground,
};

const ICON_SIZES = {
  sm: styles.sizeSm,
  md: styles.sizeMd,
};

function CircleButton({
  iconName = 'manage_accounts',
  iconNameText = '',
  className = '',
  color = null,
  label = '',
  type = 'primary',
  iconClassName = '',
  iconSize = 'md',
  loading = false,
  tooltipText = '',
  tooltipPosition = 'right',
  id = '',
  disabled = false,
  hidden = false,
  onClick = undefined,
  onKeyDown = undefined,
  tabIndex = 0,
  ...props
}) {
  const { isMobile } = useIsMobile();

  if (hidden) return null;

  const handleKeyDown = e => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled && onClick) {
      e.preventDefault();
      onClick(e);
    }
    onKeyDown?.(e);
  };

  const buttonContent = (
    <Spin spinning={loading}>
      <div className="gx-flex-column gx-justify-content-center gx-align-items-center">
        <div
          id={id || undefined}
          role="button"
          tabIndex={disabled ? -1 : tabIndex}
          aria-disabled={disabled}
          onClick={disabled ? undefined : onClick}
          onKeyDown={handleKeyDown}
          className={classNames(
            styles.circleMain,
            BACKGROUND_TYPES[type] ?? BACKGROUND_TYPES.primary,
            ICON_SIZES[iconSize],
            { [styles.disabled]: disabled },
            className,
          )}
          {...props}
        >
          {iconNameText ? (
            <span
              style={color ? { color } : undefined}
              className={classNames(
                COLOR_TYPES[type] ?? styles.primary,
                'gx-fs-md',
                iconClassName,
              )}
            >
              {iconNameText}
            </span>
          ) : (
            <span
              style={color ? { color } : undefined}
              className={classNames(
                'material-symbols-outlined',
                COLOR_TYPES[type] ?? styles.primary,
                iconClassName,
              )}
            >
              {iconName}
            </span>
          )}
        </div>
        {label && <div className="gx-d-flex gx-justify-content-center">{label}</div>}
      </div>
    </Spin>
  );

  if (tooltipText) {
    return (
      <Tooltip title={!isMobile ? tooltipText : undefined} placement={tooltipPosition}>
        {buttonContent}
      </Tooltip>
    );
  }

  return buttonContent;
}

CircleButton.propTypes = {
  iconName: PropTypes.string,
  iconNameText: PropTypes.string,
  className: PropTypes.string,
  color: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  type: PropTypes.oneOf(['primary', 'secondary', 'danger', 'dark', 'soft', 'default', 'light', 'white']),
  iconClassName: PropTypes.string,
  iconSize: PropTypes.oneOf(['sm', 'md']),
  loading: PropTypes.bool,
  tooltipText: PropTypes.oneOfType([PropTypes.string, PropTypes.node, PropTypes.element]),
  tooltipPosition: PropTypes.string,
  id: PropTypes.string,
  disabled: PropTypes.bool,
  hidden: PropTypes.bool,
  onClick: PropTypes.func,
  onKeyDown: PropTypes.func,
  tabIndex: PropTypes.number,
};

export default CircleButton;
